import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../../app/helpers";
import { CompanyCardDescriptionIndependentBookings } from "../partial/CompanyCard-Description-IndependentBookings.partial";
import { RootState } from "../../../../app/rootReducer";
import { BookingType } from "../../../Booking-Form/typings/booking-inputs";
import { IndependentBookingMode, MetaData } from "../../../Login/typings/login.types";
import { SettingsAccordion } from "./CompanyCard-Setting-Accordion.partial";
import { IndependentBookingsPerType } from "./CompanyCard-Setting-IndependentBookingsPerType.partial";
import {
  getVisibleIndependentBookingRows,
  normalizeIndependentBookingSettings,
  updateIndependentBookingMode
} from "./company-card-independent-bookings.config";

type IndependentBookingsAccordionProps = {
  newValues: MetaData;
  setNewValues: Dispatch<SetStateAction<MetaData>>;
};

export function IndependentBookingsAccordion({
  newValues,
  setNewValues
}: Readonly<IndependentBookingsAccordionProps>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    userInformation: { placeTypes, zoneTypes, visitorManagementEnabled }
  } = useSelector((state: RootState) => state.login);

  const visibleRows = useMemo(
    () =>
      getVisibleIndependentBookingRows({
        placeTypeIds: new Set(placeTypes.map(pt => pt.id)),
        zoneTypeIds: new Set(zoneTypes.map(zt => zt.id)),
        visitorManagementEnabled
      }),
    [visitorManagementEnabled, placeTypes, zoneTypes]
  );

  const visibleTypes = useMemo(
    () => visibleRows.flatMap(row => (row.bookingType != null ? [row.bookingType] : [])),
    [visibleRows]
  );

  useEffect(() => {
    if (visibleTypes.length === 0) {
      return;
    }
    setNewValues(prevState => {
      const normalized = normalizeIndependentBookingSettings(
        prevState.independentBookingSettings,
        visibleTypes
      );
      const current = prevState.independentBookingSettings;
      if (
        current?.length === normalized.length &&
        normalized.every(
          (setting, index) =>
            current[index]?.bookingType === setting.bookingType &&
            current[index]?.mode === setting.mode
        )
      ) {
        return prevState;
      }
      return { ...prevState, independentBookingSettings: normalized };
    });
  }, [setNewValues, visibleTypes]);

  const settings = normalizeIndependentBookingSettings(
    newValues.independentBookingSettings,
    visibleTypes
  );

  const handleModeChange = (bookingType: BookingType, mode: IndependentBookingMode) => {
    setNewValues(prevState => ({
      ...prevState,
      independentBookingSettings: updateIndependentBookingMode(
        prevState.independentBookingSettings,
        bookingType,
        mode,
        visibleTypes
      )
    }));
  };

  if (visibleRows.length === 0) {
    return null;
  }

  return (
    <Box data-testid="independentBookings-accordion">
      <SettingsAccordion
        title={t("_companyCard_independentBookings")}
        description={<CompanyCardDescriptionIndependentBookings />}
      >
        <Stack spacing={0}>
          <IndependentBookingsPerType
            visibleRows={visibleRows}
            settings={settings}
            isMobile={isMobile}
            t={t}
            onModeChange={handleModeChange}
          />
        </Stack>
      </SettingsAccordion>
    </Box>
  );
}
